import React, { useMemo } from "react";
import { metricsColors } from "./MetricsBars";

interface PieSegment {
    label: string;
    value: number;
    color?: string;
    testId?: string;
    onClick?: () => void;
}

interface MetricsPieProps {
    label: string;
    segments: PieSegment[];
}

const MetricsPie: React.FC<MetricsPieProps> = ({ label, segments }) => {
    const nonZeroSegments = useMemo(
        () => segments.filter((s) => s.value > 0),
        [segments]
    );

    const allZero = useMemo(
        () => nonZeroSegments.reduce((sum, s) => sum + s.value, 0) === 0,
        [nonZeroSegments]
    );

    // Hybrid slice strategy: a fixed baseline angle
    // per segment guarantees visibility, and the remaining budget is split
    // proportionally so smaller values still scale relative to each other
    // (e.g. 6 appears wider than 1).
    const BASELINE_DEGREES = 20;

    const slices = useMemo(() => {
        const source = allZero
            ? segments.map(s => ({ ...s, value: 1 }))
            : nonZeroSegments;
        const sourceTotal = source.reduce((sum, s) => sum + s.value, 0);
        const count = source.length;
        const baselineTotal = count * BASELINE_DEGREES;
        const proportionalBudget = Math.max(360 - baselineTotal, 0);

        let cumulativeAngle = 0;
        return source.map((segment, index) => {
            const sliceAngle =
                count === 1
                    ? 360
                    : BASELINE_DEGREES + (segment.value / sourceTotal) * proportionalBudget;
            const startAngle = cumulativeAngle;
            cumulativeAngle += sliceAngle;
            return {
                ...segment,
                color: allZero
                    ? "#f2f2fc"
                    : (segment.color ?? metricsColors[metricsColors.length - 1 - (index % metricsColors.length)]),
                sliceAngle,
                startAngle,
            };
        });
    }, [nonZeroSegments, allZero, segments]);

    const size = 200;
    const cx = size / 2;
    const cy = size / 2;
    const radius = size / 2 - 2;

    const degToRad = (deg: number) => (deg * Math.PI) / 180;

    const describeArc = (startAngle: number, sliceAngle: number) => {
        const start = degToRad(startAngle - 90);
        const end = degToRad(startAngle + sliceAngle - 90);
        const x1 = cx + radius * Math.cos(start);
        const y1 = cy + radius * Math.sin(start);
        const x2 = cx + radius * Math.cos(end);
        const y2 = cy + radius * Math.sin(end);
        const largeArc = sliceAngle > 180 ? 1 : 0;

        return `M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;
    };

    const getLabelPosition = (startAngle: number, sliceAngle: number) => {
        const midAngle = degToRad(startAngle + sliceAngle / 2 - 90);
        const labelRadius = radius * 0.6;
        return {
            x: cx + labelRadius * Math.cos(midAngle),
            y: cy + labelRadius * Math.sin(midAngle),
        };
    };

    const getContrastColor = (hexColor: string): string => {
        const r = parseInt(hexColor.slice(1, 3), 16);
        const g = parseInt(hexColor.slice(3, 5), 16);
        const b = parseInt(hexColor.slice(5, 7), 16);
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        return luminance > 0.5 ? "#000000" : "#ffffff";
    };

    return (
        <div className={`metrics-pie${allZero ? " metrics-pie--empty" : ""}`}>
            <div className="metrics-pie__content">
                <div className="metrics-pie__label">{label}</div>
                <div className="metrics-pie__legend">
                    {slices.map((slice, index) => (
                        <div
                            key={index}
                            className="metrics-pie__legend-item"
                            onClick={!allZero && slice.onClick ? slice.onClick : undefined}
                            data-testid={!allZero ? slice.testId : undefined}
                        >
                            <span
                                className="metrics-color-swatch"
                                style={{ backgroundColor: slice.color }}
                            />
                            <span className={`metrics-pie__legend-text${!allZero && slice.onClick ? " metrics-pie__legend-text--clickable" : ""}`}>
                                {allZero ? "\u00A0" : slice.label}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
            <div className="metrics-pie__chart">
                <svg viewBox={`0 0 ${size} ${size}`} width="100%" height="100%">
                    {slices.map((slice, index) => {
                        if (slices.length === 1) {
                            return (
                                <g key={index}>
                                    <circle
                                        cx={cx}
                                        cy={cy}
                                        r={radius}
                                        fill={slice.color}
                                        stroke="#ffffff"
                                        strokeWidth={2}
                                    />
                                    {!allZero && (
                                        <text
                                            x={cx}
                                            y={cy}
                                            textAnchor="middle"
                                            dominantBaseline="central"
                                            fill={getContrastColor(slice.color)}
                                            fontSize="20"
                                            fontWeight="600"
                                        >
                                            {slice.value}
                                        </text>
                                    )}
                                </g>
                            );
                        }

                        const labelPos = getLabelPosition(slice.startAngle, slice.sliceAngle);
                        return (
                            <g key={index}>
                                <path
                                    d={describeArc(slice.startAngle, slice.sliceAngle)}
                                    fill={slice.color}
                                    stroke="#ffffff"
                                    strokeWidth={2}
                                />
                                {!allZero && (
                                    <text
                                        x={labelPos.x}
                                        y={labelPos.y}
                                        textAnchor="middle"
                                        dominantBaseline="central"
                                        fill={getContrastColor(slice.color)}
                                        fontSize="16"
                                        fontWeight="600"
                                    >
                                        {slice.value}
                                    </text>
                                )}
                            </g>
                        );
                    })}
                </svg>
            </div>
        </div>
    );
};

export default MetricsPie;


